import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { useApi, urlMethods } from '../../../hooks/useApi';
import { usePrevious } from '../../../hooks/usePrevious';
import styles from './RecoveryGoalSettingReport.module.css';
import commonStrings from '../../../resources/commonStrings';
import Settings from '../../../resources/settings';
import tabDetails from '../../Navigation/tabDetails';
import SchoolsSelection from './SchoolsSelection';
import TermSelection from './TermSelection';
import { getBatchPrintRecoveryGoalSettingURI } from '../../../http-clients/batch-print-api';
import { getSchoolsURI } from '../../../http-clients/reporting-api';
import ReportGenerationErrorBanner from '../ReportGenerationErrorBanner';

const propTypes = {
  educatorBid: PropTypes.string.isRequired,
  partnerBid: PropTypes.string.isRequired,
  sessionToken: PropTypes.shape({ headers: PropTypes.shape({ Authorization: PropTypes.string.isRequired }) })
    .isRequired,
  userName: PropTypes.string.isRequired,
};

function RecoveryGoalSettingReport({ educatorBid, partnerBid, sessionToken, userName }) {
  const [termSelection, setTermSelection] = useState(null);
  const [schoolSelection, setSchoolSelection] = useState([]);
  const [postRequest, setPostRequest] = useState(false);
  const [allSchoolBids, setAllSchoolBids] = useState([]);
  const [reportGenerationSuccessful, setReportGenerationSuccessful] = useState(true);
  const previousTermSelection = usePrevious(termSelection);
  const shouldSchoolsCallExecute = previousTermSelection !== undefined && previousTermSelection !== termSelection;

  const [{ data: schoolsData, isLoading }] = useApi(
    {
      ...sessionToken,
      ...{
        method: urlMethods.get,
        url: getSchoolsURI(partnerBid),
        params: {
          educator: educatorBid,
          term: termSelection && termSelection.additional.currentTerm,
        },
      },
    },
    [],
    shouldSchoolsCallExecute
  );

  const onSchoolSelectAll = (isChecked) => {
    if (isChecked) {
      setSchoolSelection(allSchoolBids);
    } else {
      setSchoolSelection([]);
    }
  };

  useEffect(() => {
    if (!isLoading && termSelection && schoolsData && schoolsData.length > 0) {
      const allBids = schoolsData.reduce((acc, item) => {
        acc.push(item.bid);
        return acc;
      }, []);
      setAllSchoolBids(allBids);
      setSchoolSelection(allBids);
    } else {
      setAllSchoolBids([]);
      setSchoolSelection([]);
    }
  }, [termSelection, isLoading]);

  const onSchoolItemCheck = (schoolBid, isChecked) => {
    let newState = schoolSelection.concat();
    if (isChecked) {
      // istanbul ignore else
      if (!newState.includes(schoolBid)) {
        newState.push(schoolBid);
      }
    } else {
      // istanbul ignore else
      if (newState.includes(schoolBid)) {
        newState = newState.filter((itemBid) => itemBid !== schoolBid);
      }
    }
    setSchoolSelection(newState);
  };

  const [{ data: postResponse, isError: postIsError }] = useApi(
    {
      ...sessionToken,
      ...{
        method: urlMethods.post,
        url: getBatchPrintRecoveryGoalSettingURI(),
        data: {
          currentTermBid: termSelection && termSelection.additional.currentTerm,
          educatorBid: educatorBid,
          partnerBid: partnerBid,
          previousTermBid: termSelection && termSelection.value,
          schoolBids: schoolSelection,
          futureTermBid: termSelection && termSelection.additional.springTerm,
          termSelection: termSelection && termSelection.displayText,
          userName: userName,
        },
      },
    },
    null,
    postRequest
  );

  if (postRequest && (postResponse || postIsError)) {
    if (postIsError) {
      setReportGenerationSuccessful(false);
      setPostRequest(false);
    } else {
      if (`${postResponse.status}` === 'success') {
        //redirect to the queue
        window.location.href = `${Settings.URL_BASE_PATH}${tabDetails.reportsQueue.urlEndPoint}`;
      } else {
        setReportGenerationSuccessful(false);
        setPostRequest(false);
      }
    }
  }

  const handleReportGeneration = () => {
    // istanbul ignore else
    if (!postRequest && termSelection && schoolSelection) {
      setPostRequest(true);
    }
  };

  const isDisabled = () => {
    return (
      <>
        {reportGenerationSuccessful ? null : <ReportGenerationErrorBanner />}
        <main className={styles['recovery-goal-setting-container']}>
          <TermSelection
            partnerBid={partnerBid}
            setTermSelection={termSelectionHandler}
            sessionToken={sessionToken}
            disabled={!reportGenerationSuccessful}
          />
          <SchoolsSelection
            selectedSchoolBids={schoolSelection}
            schools={schoolsData ? schoolsData : []}
            onSchoolItemCheck={onSchoolItemCheck}
            onSelectAll={onSchoolSelectAll}
            disabled={!reportGenerationSuccessful}
          />
          <button
            disabled={isDisabled()}
            type="button"
            tabIndex={0}
            onClick={handleReportGeneration}
            onKeyPress={handleReportGeneration}
            className={`nwea-ds-v1-button-base`}
          >
            <span tabIndex="-1">{commonStrings.recoveryGoalSettingReport.createButton}</span>
          </button>
        </main>
      </>
    );
  };

  const termSelectionHandler = reportGenerationSuccessful ? setTermSelection : () => {};

  return (
    <>
      {reportGenerationSuccessful ? null : <ReportGenerationErrorBanner />}
      <main className={styles['recovery-goal-setting-container']}>
        <TermSelection
          partnerBid={partnerBid}
          setTermSelection={termSelectionHandler}
          sessionToken={sessionToken}
          disabled={!reportGenerationSuccessful}
        />
        <SchoolsSelection
          selectedSchoolBids={schoolSelection}
          schools={schoolsData ? schoolsData : []}
          onSchoolItemCheck={onSchoolItemCheck}
          onSelectAll={onSchoolSelectAll}
          disabled={!reportGenerationSuccessful}
        />
        <button
          disabled={isDisabled()}
          type="button"
          tabIndex={0}
          onClick={handleReportGeneration}
          onKeyPress={handleReportGeneration}
          className={`${buttonStyles['base']} ${buttonStyles['solid']} ${buttonStyles['purple']} ${buttonStyles['aaa']}`}
        >
          <span tabIndex="-1">{commonStrings.recoveryGoalSettingReport.createButton}</span>
        </button>
      </main>
    </>
  );
}

RecoveryGoalSettingReport.propTypes = propTypes;

export default RecoveryGoalSettingReport;
